import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jobCardsApi, employeesApi } from "@/services/api";

type AppTab = "home" | "checkin" | "jobs" | "photos" | "profile";

interface CheckInState {
  isCheckedIn: boolean;
  jobId: number | null;
  checkInTime: string | null;
  location: string;
  coords: string;
}

interface UploadedPhoto {
  id: number;
  url: string;
  caption: string;
  timestamp: string;
  jobNo: string;
}

export default function FieldAppPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<AppTab>("home");
  const [activeJobs, setActiveJobs] = useState<{ id: number; jobNo: string; title: string; location: string; status: string; progress: number; dueDate: string }[]>([]);
  const [currentEmployee, setCurrentEmployee] = useState({ name: "Field User", role: "Field Surveyor", department: "Field Operations", avatar: "FU", empId: "EMP-001", email: "", phone: "", utilization: 0, activeJobs: 0, completedJobs: 0, skills: [] as string[] });

  useEffect(() => {
    jobCardsApi.getAll({ limit: 50 }).then((res) => {
      const list = Array.isArray(res) ? res : res.data ?? [];
      setActiveJobs(list.filter((j: { status: string }) => j.status === "In Progress" || j.status === "Pending"));
    }).catch(() => setActiveJobs([]));
    employeesApi.getAll().then((res) => {
      const list = Array.isArray(res) ? res : res.data ?? [];
      if (list.length > 0) setCurrentEmployee(list[0]);
    }).catch(() => {});
  }, []);
  const [checkIn, setCheckIn] = useState<CheckInState>({
    isCheckedIn: false,
    jobId: null,
    checkInTime: null,
    location: "",
    coords: "",
  });
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsLocation, setGpsLocation] = useState("");
  const [gpsCoords, setGpsCoords] = useState("");
  const [fieldNotes, setFieldNotes] = useState("");
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [photoCaption, setPhotoCaption] = useState("");
  const [jobStatusUpdate, setJobStatusUpdate] = useState<Record<number, number>>({});
  const [successMsg, setSuccessMsg] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const captureGPS = () => {
    setGpsLoading(true);
    setTimeout(() => {
      setGpsCoords("17.3850° N, 78.4867° E");
      setGpsLocation("Shamshabad Industrial Zone, Hyderabad, Telangana");
      setGpsLoading(false);
    }, 1500);
  };

  const handleCheckIn = () => {
    if (!selectedJob || !gpsLocation) return;
    const job = activeJobs.find((j) => j.id === selectedJob);
    setCheckIn({
      isCheckedIn: true,
      jobId: selectedJob,
      checkInTime: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
      location: gpsLocation,
      coords: gpsCoords,
    });
    showSuccess(`Checked in to ${job?.title}`);
    setTab("home");
  };

  const handleCheckOut = () => {
    setCheckIn({ isCheckedIn: false, jobId: null, checkInTime: null, location: "", coords: "" });
    showSuccess("Checked out successfully");
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach((file, i) => {
      const url = URL.createObjectURL(file);
      const job = activeJobs.find((j) => j.id === (checkIn.jobId || selectedJob || 1)) || activeJobs[0];
      setPhotos((prev) => [
        ...prev,
        {
          id: Date.now() + i,
          url,
          caption: photoCaption || file.name,
          timestamp: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }),
          jobNo: job?.jobNo || "N/A",
        },
      ]);
    });
    setPhotoCaption("");
    showSuccess(`${files.length} photo(s) uploaded`);
  };

  const updateJobProgress = (jobId: number, progress: number) => {
    setJobStatusUpdate((prev) => ({ ...prev, [jobId]: progress }));
    showSuccess("Job progress updated");
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const checkedInJob = checkIn.jobId ? activeJobs.find((j) => j.id === checkIn.jobId) : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col max-w-md mx-auto relative" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Status Bar */}
      <div className="bg-amber-600 text-white px-4 py-2 flex items-center justify-between text-xs">
        <span className="font-semibold">CADSoft Field App</span>
        <div className="flex items-center gap-2">
          <span>{new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-wifi-line text-sm" />
          </span>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-battery-2-charge-line text-sm" />
          </span>
        </div>
      </div>

      {/* Success Toast */}
      {successMsg && (
        <div className="fixed top-12 left-1/2 -translate-x-1/2 z-50 bg-amber-600 text-white text-sm px-4 py-2 rounded-full font-medium animate-bounce">
          <i className="ri-checkbox-circle-line mr-1" /> {successMsg}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        {/* HOME TAB */}
        {tab === "home" && (
          <div className="p-4 space-y-4">
            {/* Greeting */}
            <div className="bg-amber-600 rounded-2xl p-4 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-lg">
                  {currentEmployee.avatar}
                </div>
                <div>
                  <p className="font-bold text-base">Good Morning, {currentEmployee.name.split(" ")[0]}!</p>
                  <p className="text-amber-100 text-xs">{currentEmployee.role} · {currentEmployee.department}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div className="bg-white/10 rounded-xl p-2 text-center">
                  <p className="text-xl font-bold">{currentEmployee.activeJobs}</p>
                  <p className="text-xs text-amber-100">Active Jobs</p>
                </div>
                <div className="bg-white/10 rounded-xl p-2 text-center">
                  <p className="text-xl font-bold">{currentEmployee.completedJobs}</p>
                  <p className="text-xs text-amber-100">Completed</p>
                </div>
                <div className="bg-white/10 rounded-xl p-2 text-center">
                  <p className="text-xl font-bold">{currentEmployee.utilization}%</p>
                  <p className="text-xs text-amber-100">Utilization</p>
                </div>
              </div>
            </div>

            {/* Check-in Status */}
            {checkIn.isCheckedIn ? (
              <div className="bg-white rounded-2xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                  </span>
                  <span className="text-sm font-semibold text-amber-700">Currently Checked In</span>
                  <span className="ml-auto text-xs text-gray-400">{checkIn.checkInTime}</span>
                </div>
                <p className="text-sm font-medium text-gray-800 mb-1">{checkedInJob?.title}</p>
                <p className="text-xs text-gray-500 mb-1">
                  <i className="ri-map-pin-line mr-1" />{checkIn.location}
                </p>
                <p className="text-xs text-gray-400 mb-3">
                  <i className="ri-crosshair-2-line mr-1" />{checkIn.coords}
                </p>
                <button
                  onClick={handleCheckOut}
                  className="w-full bg-red-50 text-red-600 border border-red-200 rounded-xl py-2.5 text-sm font-semibold cursor-pointer hover:bg-red-100 transition-all whitespace-nowrap"
                >
                  <i className="ri-logout-box-r-line mr-2" />Check Out
                </button>
              </div>
            ) : (
              <button
                onClick={() => setTab("checkin")}
                className="w-full bg-amber-600 text-white rounded-2xl py-4 text-base font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap"
              >
                <i className="ri-map-pin-user-line mr-2 text-lg" />
                Check In to Job Site
              </button>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setTab("photos")}
                className="bg-white rounded-2xl p-4 border border-gray-100 flex flex-col items-center gap-2 cursor-pointer hover:border-amber-200 transition-all"
              >
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <i className="ri-camera-line text-2xl text-amber-500" />
                </div>
                <span className="text-sm font-semibold text-gray-700">Upload Photos</span>
                <span className="text-xs text-gray-400">{photos.length} uploaded</span>
              </button>
              <button
                onClick={() => setTab("jobs")}
                className="bg-white rounded-2xl p-4 border border-gray-100 flex flex-col items-center gap-2 cursor-pointer hover:border-amber-200 transition-all"
              >
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <i className="ri-file-list-3-line text-2xl text-amber-500" />
                </div>
                <span className="text-sm font-semibold text-gray-700">My Jobs</span>
                <span className="text-xs text-gray-400">{activeJobs.length} active</span>
              </button>
            </div>

            {/* Today's Jobs */}
            <div className="bg-white rounded-2xl p-4 border border-gray-100">
              <p className="text-sm font-semibold text-gray-800 mb-3">Today&apos;s Assignments</p>
              <div className="space-y-2">
                {activeJobs.slice(0, 3).map((j) => (
                  <div
                    key={j.id}
                    onClick={() => setTab("jobs")}
                    className="flex items-center gap-3 p-2 rounded-xl bg-gray-50 cursor-pointer hover:bg-amber-50 transition-all"
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${j.status === "In Progress" ? "bg-amber-500" : "bg-amber-400"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{j.title}</p>
                      <p className="text-xs text-gray-400">{j.location}</p>
                    </div>
                    <span className="text-xs text-gray-400">{j.progress}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CHECK-IN TAB */}
        {tab === "checkin" && (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => setTab("home")} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer">
                <i className="ri-arrow-left-line text-gray-600" />
              </button>
              <h2 className="text-base font-bold text-gray-900">Check In to Job Site</h2>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-4">
              {/* Job selector */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Select Job</label>
                <select
                  value={selectedJob || ""}
                  onChange={(e) => setSelectedJob(Number(e.target.value))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
                >
                  <option value="">-- Select a job --</option>
                  {activeJobs.map((j) => (
                    <option key={j.id} value={j.id}>{j.jobNo} — {j.title.slice(0, 35)}...</option>
                  ))}
                </select>
              </div>

              {/* GPS Capture */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">GPS Location</label>
                <button
                  onClick={captureGPS}
                  disabled={gpsLoading}
                  className="w-full bg-amber-50 border border-amber-200 text-amber-700 rounded-xl py-2.5 text-sm font-semibold cursor-pointer hover:bg-amber-100 transition-all whitespace-nowrap disabled:opacity-60"
                >
                  {gpsLoading ? (
                    <><i className="ri-loader-4-line animate-spin mr-2" />Capturing GPS...</>
                  ) : (
                    <><i className="ri-crosshair-2-line mr-2" />Capture GPS Location</>
                  )}
                </button>
                {gpsCoords && (
                  <div className="mt-2 p-2 bg-amber-50 rounded-xl">
                    <p className="text-xs font-medium text-amber-700">
                      <i className="ri-map-pin-line mr-1" />{gpsLocation}
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">{gpsCoords}</p>
                  </div>
                )}
              </div>

              {/* Field Notes */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Field Notes (optional)</label>
                <textarea
                  value={fieldNotes}
                  onChange={(e) => setFieldNotes(e.target.value.slice(0, 500))}
                  placeholder="Site conditions, access notes, observations..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none"
                />
                <p className="text-xs text-gray-400 text-right">{fieldNotes.length}/500</p>
              </div>

              <button
                onClick={handleCheckIn}
                disabled={!selectedJob || !gpsLocation}
                className="w-full bg-amber-600 text-white rounded-xl py-3 text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-map-pin-user-line mr-2" />Confirm Check In
              </button>
            </div>
          </div>
        )}

        {/* JOBS TAB */}
        {tab === "jobs" && (
          <div className="p-4 space-y-4">
            <h2 className="text-base font-bold text-gray-900">My Job Assignments</h2>
            <div className="space-y-3">
              {activeJobs.map((j) => {
                const currentProgress = jobStatusUpdate[j.id] ?? j.progress;
                return (
                  <div key={j.id} className="bg-white rounded-2xl p-4 border border-gray-100">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 leading-tight">{j.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{j.jobNo}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex-shrink-0 ${
                        j.status === "In Progress" ? "bg-amber-50 text-amber-700" : "bg-amber-50 text-amber-700"
                      }`}>
                        {j.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                      <i className="ri-map-pin-line" />{j.location}
                      <span className="text-gray-300">·</span>
                      <i className="ri-calendar-line" />Due {new Date(j.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </div>

                    {/* Progress update */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">Progress</span>
                        <span className="text-xs font-bold text-amber-600">{currentProgress}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={currentProgress}
                        onChange={(e) => setJobStatusUpdate((prev) => ({ ...prev, [j.id]: Number(e.target.value) }))}
                        className="w-full accent-amber-500"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => updateJobProgress(j.id, currentProgress)}
                        className="flex-1 bg-amber-600 text-white rounded-xl py-2 text-xs font-semibold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap"
                      >
                        <i className="ri-save-line mr-1" />Update Progress
                      </button>
                      <button
                        onClick={() => { setSelectedJob(j.id); setTab("photos"); }}
                        className="flex-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl py-2 text-xs font-semibold cursor-pointer hover:bg-amber-100 transition-all whitespace-nowrap"
                      >
                        <i className="ri-camera-line mr-1" />Add Photos
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PHOTOS TAB */}
        {tab === "photos" && (
          <div className="p-4 space-y-4">
            <h2 className="text-base font-bold text-gray-900">Upload Site Photos</h2>

            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Job (for tagging)</label>
                <select
                  value={selectedJob || checkIn.jobId || ""}
                  onChange={(e) => setSelectedJob(Number(e.target.value))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
                >
                  <option value="">-- Select job --</option>
                  {activeJobs.map((j) => (
                    <option key={j.id} value={j.id}>{j.jobNo}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Caption (optional)</label>
                <input
                  type="text"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  placeholder="e.g. North boundary marker"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
                />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-amber-300 bg-amber-50 text-amber-700 rounded-2xl py-6 text-sm font-semibold cursor-pointer hover:bg-amber-100 transition-all whitespace-nowrap"
              >
                <i className="ri-camera-line text-2xl block mb-1" />
                Tap to take photo or upload
              </button>
            </div>

            {photos.length > 0 && (
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <p className="text-sm font-semibold text-gray-800 mb-3">{photos.length} Photo(s) Uploaded</p>
                <div className="grid grid-cols-2 gap-2">
                  {photos.map((p) => (
                    <div key={p.id} className="relative rounded-xl overflow-hidden">
                      <div className="w-full h-28 bg-gray-100">
                        <img src={p.url} alt={p.caption} className="w-full h-full object-cover" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                        <p className="text-white text-xs truncate">{p.caption}</p>
                        <p className="text-white/70 text-xs">{p.timestamp}</p>
                      </div>
                      <button
                        onClick={() => setPhotos((prev) => prev.filter((ph) => ph.id !== p.id))}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* PROFILE TAB */}
        {tab === "profile" && (
          <div className="p-4 space-y-4">
            <h2 className="text-base font-bold text-gray-900">My Profile</h2>
            <div className="bg-white rounded-2xl p-5 border border-gray-100 text-center">
              <div className="w-20 h-20 rounded-full bg-amber-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">
                {currentEmployee.avatar}
              </div>
              <p className="text-base font-bold text-gray-900">{currentEmployee.name}</p>
              <p className="text-sm text-gray-500">{currentEmployee.role}</p>
              <p className="text-xs text-gray-400 mt-0.5">{currentEmployee.department}</p>
              <div className="flex justify-center gap-2 mt-3 flex-wrap">
                {currentEmployee.skills.map((s) => (
                  <span key={s} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{s}</span>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-xl">
                  <i className="ri-mail-line text-gray-500 text-sm" />
                </span>
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  <p className="text-sm font-medium text-gray-800">{currentEmployee.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-xl">
                  <i className="ri-phone-line text-gray-500 text-sm" />
                </span>
                <div>
                  <p className="text-xs text-gray-400">Phone</p>
                  <p className="text-sm font-medium text-gray-800">{currentEmployee.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-xl">
                  <i className="ri-id-card-line text-gray-500 text-sm" />
                </span>
                <div>
                  <p className="text-xs text-gray-400">Employee ID</p>
                  <p className="text-sm font-medium text-gray-800">{currentEmployee.empId}</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-gray-100 text-gray-700 rounded-xl py-3 text-sm font-semibold cursor-pointer hover:bg-gray-200 transition-all whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2" />Back to Desktop ERP
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 flex items-center justify-around px-2 py-2 z-40">
        {[
          { key: "home", icon: "ri-home-5-line", label: "Home" },
          { key: "checkin", icon: "ri-map-pin-user-line", label: "Check In" },
          { key: "jobs", icon: "ri-file-list-3-line", label: "Jobs" },
          { key: "photos", icon: "ri-camera-line", label: "Photos" },
          { key: "profile", icon: "ri-user-line", label: "Profile" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as AppTab)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl cursor-pointer transition-all ${
              tab === t.key ? "text-amber-600" : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <i className={`${t.icon} text-xl`} />
            </span>
            <span className="text-xs font-medium">{t.label}</span>
            {tab === t.key && <span className="w-1 h-1 rounded-full bg-amber-500" />}
          </button>
        ))}
      </nav>
    </div>
  );
}
